import { Component, OnInit, ChangeDetectorRef, ViewEncapsulation, ViewChild } from '@angular/core';
import { DestructorComponent } from 'src/app/components/destructor/destructor.component';
import { VehicleBidsService } from 'src/app/services/bids/vehicle-bids.service';
import { GeneralService } from 'src/app/services/general/general.service';
import { Bid, PaginationInfo, PaginatedResponse } from 'src/app/shared/models';
import { VhUpdateBidComponent } from 'src/app/layout/modals/vh-update-bid/vh-update-bid.component';
import Swal from 'sweetalert2';
import { UserService } from 'src/app/services/user/user.service';
import { Router } from '@angular/router';

interface FilterOption {
  value: string;
  label: string;
  checked: boolean;
}

interface CachedMakeData {
  name: string;
  slug: string;
  count: number;
  models: { [modelSlug: string]: CachedModelData };
}

interface CachedModelData {
  name: string;
  slug: string;
  chassis_model: string[];
}

interface MakesDateStructure {
  [date: string]: {
    [makeSlug: string]: CachedMakeData;
  };
}

interface BidManagerFilters {
  dates: string[];
  makes: string[];
  salesPersons: string[];
  statuses: string[];
  lotNumbers: string;
}

@Component({
  selector: 'app-vh-bid-manager',
  templateUrl: './vh-bid-manager.component.html',
  styleUrls: ['./vh-bid-manager.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class VhBidManagerComponent extends DestructorComponent implements OnInit {

  @ViewChild('updateBidModal') updateBidModal!: VhUpdateBidComponent;

  // Expose Math and parseInt to template
  Math = Math;
  parseInt = parseInt;

  // Data properties
  bids: Bid[] = [];
  paginationInfo: PaginationInfo = {
    currentPage: 1,
    perPage: 20,
    total: 0,
    lastPage: 1,
    firstPage: 1
  };

  // State properties
  isLoading: boolean = false;
  currentPage: number = 1;
  showFiltersForm: boolean = true;
  isExportingExcel: boolean = false;

  // Selection properties
  selectedBids: string[] = [];

  salesPersonList: any[] = [];

  // Filter properties
  filters: BidManagerFilters = {
    dates: [],
    makes: [],
    salesPersons: [],
    statuses: [],
    lotNumbers: ''
  };

  // Filter options
  dateOptions: FilterOption[] = [];
  makeOptions: FilterOption[] = [];
  salesPersonOptions: FilterOption[] = [];
  statusOptions: FilterOption[] = [
    { value: '', label: 'All Status', checked: false },
    { value: 'pending', label: 'Pending', checked: false },
    { value: 'purchased', label: 'Purchased', checked: false },
    { value: 'cancelled', label: 'Cancelled', checked: false }
  ];

  // Cached data for smart filtering
  makesDateData: MakesDateStructure = {};
  isLoadingCachedData = false;

  // Loading states
  isLoadingMakes = false;

  constructor(
    private _vehicleBidsService: VehicleBidsService,
    private _generalService: GeneralService,
    private cdr: ChangeDetectorRef,
    private _userService: UserService,
    private router: Router
  ) {
    super();
  }

  ngOnInit(): void {
    this.generateDateOptions();
    this.loadCachedMakeData();
    this.loadBids();
    this.GetSalesPersonList();
  }

  private resetPagination(): void {
    this.paginationInfo = {
      currentPage: 1,
      perPage: 20,
      total: 0,
      lastPage: 1,
      firstPage: 1
    };
  }

  loadBids(page: number = 1, search: string = '', filters: Record<string, any> = {}): void {
    this.isLoading = true;

    // Merge current filters with passed filters
    const apiFilters = { ...this.getFilterParams(), ...filters };

    // Remove empty filters
    Object.keys(apiFilters).forEach(key => {
      if (!apiFilters[key]) {
        delete apiFilters[key];
      }
    });

    this.subscriptions.push(
      this._vehicleBidsService.GetData(page.toString(), search, apiFilters).subscribe({
        next: (response: PaginatedResponse<Bid>) => {
          if (response.status && response.data) {
            this.bids = response.data.data || [];
            this.paginationInfo = response.data.pagination;
            this.currentPage = response.data.pagination.currentPage;
          } else {
            this.bids = [];
            this.resetPagination();
          }
          // Clear selection when new data is loaded
          this.selectedBids = [];
          this.isLoading = false;
          this.cdr.detectChanges();
        },
        error: (error) => {
          console.error('Error loading bids:', error);
          this.bids = [];
          this.resetPagination();
          this.isLoading = false;
          this.cdr.detectChanges();
        }
      })
    );
  }

  private GetSalesPersonList() {
    this.subscriptions.push(
      this._userService.GetData('1', '', { role_type: 30, paginate: 'false' }).subscribe({
        next: (response: any) => {
          if (response.status && response.data) {
            this.salesPersonList = response.data;
            this.populateSalesPersonOptions();
          }
        },
        error: (error: any) => {
          console.error('Error loading sales person list:', error);
        }
      })
    );
  }

  // Populate sales person options from API data
  private populateSalesPersonOptions(): void {
    this.salesPersonOptions = this.salesPersonList.map((person: any) => ({
      value: person._id,
      label: person.name,
      checked: false
    }));
    console.log('Sales person options populated:', this.salesPersonOptions.length);
  }

  // Generate date options for next 7 days
  generateDateOptions(): void {
    const today = new Date();
    this.dateOptions = [];
    this.filters.dates = []; // Initialize dates filter array

    for (let i = 0; i < 7; i++) {
      const date = new Date(today);
      date.setDate(today.getDate() + i);

      const dateString = date.toISOString().split('T')[0];
      const label = i === 0 ? 'Today' : i === 1 ? 'Tomorrow' : date.toLocaleDateString('en-US', {
        weekday: 'short',
        month: 'short',
        day: 'numeric'
      });

      this.dateOptions.push({
        value: dateString,
        label: label,
        checked: true // All dates checked by default
      });

      // Add to filters array since all are checked by default
      this.filters.dates.push(dateString);
    }
    console.log('Date options generated with all checked:', this.filters.dates.length);
  }

  // Load cached make data from API (called once)
  private loadCachedMakeData(): void {
    this.isLoadingCachedData = true;
    this.isLoadingMakes = true;

    this.subscriptions.push(
      this._generalService.GetCachedData('alaedo-data').subscribe({
        next: (response: any) => {
          if (response.status && response.data && response.data.make_dates) {
            this.makesDateData = response.data.make_dates;
            console.log('Cached make data loaded:', Object.keys(this.makesDateData).length, 'dates');
            this.populateMakeOptions();
          }
          this.isLoadingCachedData = false;
          this.isLoadingMakes = false;
        },
        error: (error: any) => {
          console.error('Error loading cached make data:', error);
          this.isLoadingCachedData = false;
          this.isLoadingMakes = false;
        }
      })
    );
  }

  // Populate make options from cached data based on selected dates
  private populateMakeOptions(): void {
    const selectedDates = this.filters.dates;
    if (selectedDates.length === 0) {
      this.makeOptions = [];
      return;
    }

    // Aggregate makes across selected dates
    const makeMap: { [makeSlug: string]: { name: string; count: number } } = {};

    selectedDates.forEach(date => {
      if (this.makesDateData[date]) {
        Object.keys(this.makesDateData[date]).forEach(makeSlug => {
          const make = this.makesDateData[date][makeSlug];
          if (makeMap[makeSlug]) {
            // Make already exists, add to count
            makeMap[makeSlug].count += make.count;
          } else {
            // New make, initialize
            makeMap[makeSlug] = {
              name: make.name,
              count: make.count
            };
          }
        });
      }
    });

    // Create filter options
    this.makeOptions = Object.keys(makeMap).map(makeSlug => ({
      value: makeSlug,
      label: `${makeMap[makeSlug].name} (${makeMap[makeSlug].count})`,
      checked: false
    }));

    // Sort makes alphabetically by name
    this.makeOptions.sort((a, b) => {
      const nameA = makeMap[a.value].name.toLowerCase();
      const nameB = makeMap[b.value].name.toLowerCase();
      return nameA.localeCompare(nameB);
    });

    console.log('Make options populated:', this.makeOptions.length, 'for dates:', selectedDates);
  }

  onPageChange(page: number): void {
    this.loadBids(page, '', this.getFilterParams());
  }

  toggleFilters(): void {
    this.showFiltersForm = !this.showFiltersForm;
  }

  // Handle filter changes
  onFilterChange(filterType: keyof BidManagerFilters, value: string, checked: boolean): void {
    // Handle lotNumbers separately as it's a string, not an array
    if (filterType === 'lotNumbers') {
      this.filters.lotNumbers = value;
      console.log(`${filterType} filter:`, this.filters[filterType]);
      return;
    }

    const filterArray = this.filters[filterType] as string[];

    if (checked) {
      if (!filterArray.includes(value)) {
        filterArray.push(value);
      }
    } else {
      const index = filterArray.indexOf(value);
      if (index > -1) {
        filterArray.splice(index, 1);
      }
    }

    // Update the checkbox state
    if (filterType === 'dates') {
      const option = this.dateOptions.find(opt => opt.value === value);
      if (option) option.checked = checked;

      // Refresh makes when dates change
      this.populateMakeOptions();

      // Clear dependent filters (makes)
      this.filters.makes = [];
      this.makeOptions.forEach(opt => opt.checked = false);
    } else if (filterType === 'makes') {
      const option = this.makeOptions.find(opt => opt.value === value);
      if (option) option.checked = checked;
    } else if (filterType === 'salesPersons') {
      const option = this.salesPersonOptions.find(opt => opt.value === value);
      if (option) option.checked = checked;
    } else if (filterType === 'statuses') {
      const option = this.statusOptions.find(opt => opt.value === value);
      if (option) option.checked = checked;
    }

    console.log(`${filterType} filters:`, this.filters[filterType]);
  }

  applyFilters(): void {
    console.log('Applied filters:', this.filters);
    this.currentPage = 1;
    this.loadBids(1, '', this.getFilterParams());
  }

  // Check if all dates are checked
  isAllDatesChecked(): boolean {
    if (this.dateOptions.length === 0) {
      return false;
    }
    return this.dateOptions.every(option => option.checked);
  }

  // Handle "All" checkbox change for dates
  onDateAllChange(checked: boolean): void {
    // Update all date options
    this.dateOptions.forEach(option => {
      option.checked = checked;
    });

    // Update filters array
    if (checked) {
      // Check all - add all date values to filters
      this.filters.dates = this.dateOptions.map(option => option.value);
    } else {
      // Uncheck all - clear filters
      this.filters.dates = [];
    }

    // Refresh makes based on new dates
    this.populateMakeOptions();

    // Clear dependent filters (makes)
    this.filters.makes = [];
    this.makeOptions.forEach(option => option.checked = false);

    console.log('All dates', checked ? 'checked' : 'unchecked', ':', this.filters.dates);
  }

  clearFilters(): void {
    // Reset filter arrays
    this.filters.dates = [];
    this.filters.makes = [];
    this.filters.salesPersons = [];
    this.filters.statuses = [];
    this.filters.lotNumbers = '';

    // Reset date options - set all to checked by default
    this.dateOptions.forEach(option => {
      option.checked = true;
      this.filters.dates.push(option.value);
    });

    // Reset make options - set all to unchecked
    this.makeOptions.forEach(option => option.checked = false);

    // Reset sales person options - set all to unchecked
    this.salesPersonOptions.forEach(option => option.checked = false);

    // Reset status options - set all to unchecked
    this.statusOptions.forEach(option => option.checked = false);

    this.currentPage = 1;
    this.loadBids();
  }

  private getFilterParams(): Record<string, any> {
    const params: Record<string, any> = {};

    // Add date filters
    if (this.filters.dates.length > 0) {
      params['auction_dates'] = this.filters.dates.join(',');
    }

    // Add make filters
    if (this.filters.makes.length > 0) {
      params['makes'] = this.filters.makes.join(',');
    }

    // Add sales person filters
    if (this.filters.salesPersons.length > 0) {
      params['sales_persons'] = this.filters.salesPersons.join(',');
    }

    // Add status filters (exclude empty values for "All Status")
    const nonEmptyStatuses = this.filters.statuses.filter(status => status !== '');
    if (nonEmptyStatuses.length > 0) {
      params['statuses'] = nonEmptyStatuses.join(',');
    }

    // Add lot number filters
    if (this.filters.lotNumbers.trim()) {
      params['lot_numbers'] = this.filters.lotNumbers.trim();
    }

    return params;
  }

  // Utility methods for template
  formatDate(dateString: string): string {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: '2-digit',
      year: 'numeric'
    });
  }

  formatDateTime(dateString: string): string {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  formatCurrency(amount: number, currency: string = 'JPY'): string {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  }

  getStatusBadgeClass(status: string): string {
    switch (status.toLowerCase()) {
      case 'pending':
        return 'badge-warning';
      case 'accepted':
        return 'badge-success';
      case 'rejected':
        return 'badge-danger';
      case 'cancelled':
        return 'badge-secondary';
      default:
        return 'badge-light';
    }
  }

  getMainImage(car: any): string {
    let pictures = car?.pictures?.length
      ? [...car.pictures.slice(1), car.pictures[0]]
      : [];
    if (pictures.length > 0) {
      return pictures[0];
    }
    return 'assets/media/stock/600x400/img-1.jpg';
  }

  trackByFn(_index: number, bid: Bid): string {
    return bid.doc_id;
  }

  // Selection methods
  toggleSelection(bidId: string, event: any): void {
    if (event.target.checked) {
      if (!this.selectedBids.includes(bidId)) {
        this.selectedBids.push(bidId);
      }
    } else {
      this.selectedBids = this.selectedBids.filter(id => id !== bidId);
    }
  }

  isSelected(bidId: string): boolean {
    return this.selectedBids.includes(bidId);
  }

  toggleAllSelection(event: any): void {
    if (event.target.checked) {
      // Select all bids
      this.selectedBids = this.bids.map(bid => bid._id);
    } else {
      // Deselect all bids
      this.selectedBids = [];
    }
  }

  isAllSelected(): boolean {
    return this.bids.length > 0 && this.selectedBids.length === this.bids.length;
  }

  isIndeterminate(): boolean {
    return this.selectedBids.length > 0 && this.selectedBids.length < this.bids.length;
  }

  cancelAllSelectedBids(): void {
    if (this.selectedBids.length === 0) {
      return;
    }

    const bidCount = this.selectedBids.length;
    const bidText = bidCount === 1 ? 'bid' : 'bids';

    Swal.fire({
      title: 'Are you sure?',
      text: `You are about to cancel ${bidCount} ${bidText}. This action cannot be undone!`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: `Yes, cancel ${bidCount} ${bidText}!`,
      cancelButtonText: 'No, keep them',
      buttonsStyling: false,
      customClass: {
        confirmButton: 'btn fw-bold btn-danger',
        cancelButton: 'btn fw-bold btn-active-light-primary'
      }
    }).then((result) => {
      if (result.isConfirmed) {
        // User confirmed - proceed with cancellation
        console.log('Selected bid IDs for cancellation:', this.selectedBids);

        // Here you would typically call an API to cancel the bids
        // For now, just logging the IDs as requested

        // Show success message
        Swal.fire({
          title: 'Cancelled!',
          text: `${bidCount} ${bidText} have been cancelled successfully.`,
          icon: 'success',
          confirmButtonText: 'Ok, got it!',
          buttonsStyling: false,
          customClass: {
            confirmButton: 'btn fw-bold btn-primary'
          }
        }).then(() => {
          // Clear selection after successful cancellation
          this.selectedBids = [];
          // Optionally reload the data to reflect changes
          // this.loadBids(this.currentPage);
        });
      }
    });
  }

  // Calculate active filters count
  get activeFiltersCount(): number {
    let count = 0;

    // Count selected dates (only if not all dates are selected)
    const totalDates = this.dateOptions.length;
    const selectedDates = this.filters.dates.length;
    if (selectedDates > 0 && selectedDates < totalDates) {
      count++;
    }

    // Count selected makes
    if (this.filters.makes.length > 0) {
      count++;
    }

    // Count selected sales persons
    if (this.filters.salesPersons.length > 0) {
      count++;
    }

    // Count selected statuses
    if (this.filters.statuses.length > 0) {
      count++;
    }

    // Count lot numbers
    if (this.filters.lotNumbers.trim()) {
      count++;
    }

    return count;
  }

  // Action Methods
  editBid(bid: Bid): void {
    console.log('Edit bid:', bid);
    if (this.updateBidModal) {
      this.updateBidModal.openModal(bid);
    }
  }

  viewDocuments(bid: Bid): void {
    console.log('View documents for bid:', bid);
    // TODO: Implement view documents functionality
    // This could open a modal with documents or navigate to documents page
    Swal.fire({
      title: 'View Documents',
      text: `View documents for ${bid.car.company_name} ${bid.car.name} (Lot: ${bid.car.lot_no})`,
      icon: 'info',
      confirmButtonText: 'OK',
      buttonsStyling: false,
      customClass: {
        confirmButton: 'btn fw-bold btn-primary'
      }
    });
  }

  calculateCosts(bid: Bid): void {
    console.log('Calculate costs for bid:', bid);
    // TODO: Implement calculate costs functionality
    // This could open a calculator modal or navigate to cost calculation page
    Swal.fire({
      title: 'Calculate Costs',
      text: `Calculate costs for ${bid.car.company_name} ${bid.car.name} (Lot: ${bid.car.lot_no})`,
      icon: 'info',
      confirmButtonText: 'OK',
      buttonsStyling: false,
      customClass: {
        confirmButton: 'btn fw-bold btn-primary'
      }
    });
  }

  // Handle bid updated event from modal
  onBidUpdated(): void {
    console.log('Bid updated, refreshing list...');
    this.loadBids(this.currentPage);
  }

  // Format label with count styling
  formatLabelWithCount(label: string): string {
    // Extract name and count from label like "TOYOTA (35)"
    const match = label.match(/^(.+?)\s*\((\d+)\)$/);
    if (match) {
      const name = match[1];
      const count = match[2];
      return `${name}`;
      // return `${name} <span class="badge badge-light-primary ms-2">${count}</span>`;
    }
    return label;
  }

  goToVehicleDetails(bid: any): void {
    if (bid && bid.car && bid.car._id) {
      window.open('/auction/search-bids/view-bid/' + bid.car._id, '_blank');
    }
  }

  // Helper method to get unit display text
  getUnitDisplayText(bid: Bid): string {

    if(bid.bid_unit && !bid.bid_unit.number_of_units_bought){
      return `0 / ${bid.bid_unit.number_of_units}`;
    }

    if (bid.bid_unit && bid.bid_unit.number_of_units_bought !== undefined && bid.bid_unit.number_of_units !== undefined) {
      return `${bid.bid_unit.number_of_units_bought} / ${bid.bid_unit.number_of_units}`;
    }
    return 'N/A';
  }

  // Check if there are active filters
  hasActiveFilters(): boolean {
    return this.activeFiltersCount > 0;
  }

  // Export Excel method
  exportExcel(): void {
    // Set loading state
    this.isExportingExcel = true;

    // Prepare filters object
    const filtersObj = this.hasActiveFilters() ? { ...this.getFilterParams() } : {};

    // Add type parameter
    filtersObj.type = 'bid-manager';
    filtersObj.excel_name = "Bid Manager";

    console.log('Exporting Excel with filters:', filtersObj);

    // Call the generateExcelPost method
    this.subscriptions.push(
      this._generalService.generateExcelPost(filtersObj).subscribe({
        next: (response: any) => {
          this.isExportingExcel = false;
          console.log('Excel export response:', response);

          if (response.status && response.data && response.data.url) {
            // Open the Excel file URL in a new tab
            window.open(response.data.url, '_blank');
          } else {
            // Show error message if no URL is returned
            Swal.fire({
              text: 'Failed to generate Excel file. No URL received.',
              icon: "error",
              buttonsStyling: false,
              confirmButtonText: "Ok, got it!",
              customClass: {
                confirmButton: "btn fw-bold btn-primary",
              }
            });
          }
        },
        error: (error: any) => {
          this.isExportingExcel = false;
          console.error('Error exporting Excel:', error);
          Swal.fire({
            text: 'An error occurred while exporting Excel file.',
            icon: "error",
            buttonsStyling: false,
            confirmButtonText: "Ok, got it!",
            customClass: {
              confirmButton: "btn fw-bold btn-primary",
            }
          });
        }
      })
    );
  }
}
